/**
 * 页面的最小宽度(「画板」):窗口比它窄才出横向滚动条。
 *
 * 原型阶段定的是所有页面一律 1440(固定画板,不变形)。真实浏览器里这有个毛病:带滚动条的窗口在 1440 宽的屏幕上
 * 只剩 1425,每一屏底部都多出一条横向滚动条;1366 的笔记本、1280 的投影上要横着拖才看得全。
 * 而前台页面(官网、登录、学生端、家长端、考试端……)的内容列本来只有 1180 宽、两边是留白,有 1212(1180 + 两边各 16)就排得下。
 * 所以:前台页面的画板收到 1212;后台是「侧栏 232 + 主区 1208」的固定版式,仍然要 1440;评审工作台没逐页核对过,也先不动。
 *
 * 第三档 fluid(2026-10-13「精修做工」):自适应到手机的页面不设最小宽度,版式自己从宽屏一路排到手机
 * (内容列用 .ptl-wrap,窄了靠媒体查询换排法)。第一轮只有官网首页;第二轮起平台官网 / 机构站点整站都是这一档 ——
 * 首页、各内页、证书核验、协议与政策,加上状态页(404 这些)。别的页面轮到重做、做成自适应的之后再加进来。
 *
 * 这里只放纯规则;写到 <html data-board> 的那一步在 main.ts 的路由守卫里,样式在 styles/base.css。
 */
export type Board = 'fit' | 'wide' | 'fluid';

/*
 * 第三轮(2026-10-14)加进来的是入口页:登录、「请用电脑」、免登录交接、考试客户端的下载页和「请用考试客户端」那一页
 * (后者在 /exam/records/:id/ 下面,只认结尾是 use-client 的这一条 —— 同一前缀下的检录、答题、成绩页还是 1212)。
 * 招生公开页(/book、/act、/intro、/r)不在这里:它们一直是自己管 body.m-free,本来就不套画板。
 */
/** 仍然用 1440 画板的页面(路径前缀) */
const WIDE_PREFIXES = ['/admin', '/review'];
/** 自适应到手机、不设最小宽度的页面:整条路径(首页、证书核验、几个入口页)和路径前缀(官网内页 /site/…、协议与政策 /legal/…) */
const FLUID_PATHS = ['/', '/verify', '/login', '/use-desktop', '/sso', '/exam-client'];
const FLUID_PREFIXES = ['/site', '/legal'];
/** 「请用考试客户端」:/exam/records/<考试记录>/use-client,只认这一条 */
const USE_CLIENT = /^\/exam\/records\/[^/]+\/use-client$/;
/**
 * 第四轮(2026-10-14)加进来的是大赛官网首页:/c/<站点码>,只认这一条整路径(末尾带不带斜杠都算)——
 * 它的子页当时还是电脑版式(1212);第五轮起重做过的几个子页见下面的 PORTAL_SUB
 */
const PORTAL_HOME = /^\/c\/[^/]+\/?$/;
/**
 * 第五轮(2026-10-15)加进来的是大赛官网重做过的子页:通知列表与详情(news、news/<id>)、主办方自定义页(p/<key>)、
 * 成绩与证书查询(results)、证书核验(verify)、电子证书(cert/<id>)、选手登录(login)。
 * 第六轮(2026-10-18)加上报名(signup);第七轮加上个人中心(me)、教师中心(teacher);第八轮(2026-10-19)加上作品提交(work/<报名>)。
 * 线下赛(offline/…)还是电脑版式(1212),轮到重做时再加
 */
const PORTAL_SUB =
  /^\/c\/[^/]+\/(news|news\/[^/]+|p\/[^/]+|results|verify|cert\/[^/]+|login|signup|me|teacher|work\/[^/]+)\/?$/;

/** 前缀按整段算:/site 管 /site 和 /site/…,不管 /sitemap */
const under = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}/`);

/**
 * 这一页用哪一档。状态页(404 / 无权限 / 维护中 / 加载失败 / 机构暂停)几条路由共用一个自适应的页面,靠路由的
 * meta.status 认,排在路径规则前面 —— 「不存在的地址」没有固定路径,/admin/写错了 也落到它。
 */
export function boardFor(to: { path: string; meta?: { status?: unknown } }): Board {
  if (to.meta?.status) return 'fluid';
  if (FLUID_PATHS.includes(to.path) || FLUID_PREFIXES.some((p) => under(to.path, p))) return 'fluid';
  if (USE_CLIENT.test(to.path) || PORTAL_HOME.test(to.path) || PORTAL_SUB.test(to.path)) return 'fluid';
  return WIDE_PREFIXES.some((p) => under(to.path, p)) ? 'wide' : 'fit';
}

/** 写到 <html data-board>:fit / fluid 写上(样式认这两个),wide 就把属性去掉,回到令牌里的默认 1440 */
export function applyBoard(root: { dataset: Record<string, string | undefined> }, board: Board): void {
  if (board === 'wide') delete root.dataset['board'];
  else root.dataset['board'] = board;
}
